import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Suspense } from 'react';
import { useDeepLinkRedirect } from '@/hooks/useDeepLinkRedirect';
import { Toaster } from '@/components/ui/sonner';
import { CommunityKindRoute } from '@/components/routing/CommunityKindRoute';
import {
  communityPathFallbackRoute,
  sharedCommunityRoutes,
} from '@/features/community/shared-community-routes';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import CommunityLayout from '@/layouts/CommunityLayout';
import VendorProjectDetailPage from '@/pages/dashboard/vendor-project-detail-page';
import MembersPage from '@/pages/members/MembersPage';
import GivesAsksPage from '@/pages/gives-asks/GivesAsksPage';
import CreatePage from '@/pages/create/CreatePage';
import LeadPage from '@/pages/create/lead/LeadPage';
import TestimonialPage from '@/pages/create/testimonial/TestimonialPage';
import ThankYouPage from '@/pages/create/thank-you/ThankYouPage';
import MeetupPage from '@/pages/create/meetup/MeetupPage';
import GeneralPostPage from '@/pages/create/general/GeneralPostPage';
import PollPage from '@/pages/create/poll/PollPage';
import BusinessPage from '@/pages/business/BusinessPage';
import ProfilePage from '@/pages/profile/ProfilePage';
import EditProfilePage from '@/pages/profile/EditProfilePage';
import NotificationsPage from '@/pages/NotificationsPage';
import CreateCommunityPage from '@/pages/CreateCommunityPage';
import CreateSubCommunityPage from '@/pages/CreateSubCommunityPage';
import SubCommunityPage from '@/pages/SubCommunityPage';
import InvitePage from '@/pages/InvitePage';
import InviteAcceptPage from '@/pages/InviteAcceptPage';
import CreateBusinessPage from '@/pages/create/CreateBusinessPage';
import CommunitySettingsRouter from '@/pages/settings/CommunitySettingsRouter';
import UserSettingsPage from '@/pages/settings/UserSettingsPage';
import MemberRequestsPage from '@/pages/settings/MemberRequestsPage';
import MembershipPage from '@/pages/membership/MembershipPage';
import MembershipAdminPage from '@/pages/membership/MembershipAdminPage';
import PostPage from '@/pages/dashboard/PostPage';
import CommunityPostPage from '@/pages/dashboard/CommunityPostPage';
import EditPostPage from '@/pages/dashboard/EditPostPage';
import { SocialSearchPage } from '@/pages/dashboard/SocialSearchPage';
import LeadDetailPage from '@/pages/leads/LeadDetailPage';
import { RoleGuard } from '@/components/auth/RoleGuard';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { OnboardingPrompt } from '@/components/onboarding/onboarding-prompt';
import { BusinessLinkCompliancePrompt } from '@/components/onboarding/business-link-compliance-prompt';
import { PostRegisterBusinessPrompt } from '@/components/onboarding/post-register-business-prompt';
import DebugCalendarPage from '@/pages/DebugCalendarPage';
import AdminCategoriesPage from '@/pages/admin/AdminCategoriesPage';
import CreateEventPage from '@/pages/events/CreateEventPage';
import EditEventPage from '@/pages/events/EditEventPage';
import EventManagementPage from '@/pages/events/EventManagementPage';
import EventDetailsPage from '@/pages/events/EventDetailsPage';
import EventsPage from '@/pages/events/EventsPage';
import MyConclaveEventsPage from '@/pages/conclave/MyConclaveEventsPage';
import CreateStandaloneConclavePage from '@/pages/conclave/CreateStandaloneConclavePage';
import ConclaveNetworkingProfilePage from '@/pages/conclave/ConclaveNetworkingProfilePage';
import ConclaveLivePage from '@/pages/conclave/ConclaveLivePage';
import ConclaveOrganisePage from '@/pages/conclave/ConclaveOrganisePage';
import ProjectsPage from '@/pages/projects/ProjectsPage';
import CreateProjectPage from '@/pages/projects/CreateProjectPage';
import EditProjectPage from '@/pages/projects/EditProjectPage';
import ProjectDetailsPage from '@/pages/projects/ProjectDetailsPage';

import EventActivitiesPage from '@/pages/events/manage/EventActivitiesPage';
import ActivityScannerPage from '@/pages/events/manage/ActivityScannerPage';
import ActivitySelfMarkPage from '@/pages/events/ActivitySelfMarkPage';
import EventRegistrationsPage from '@/pages/events/manage/EventRegistrationsPage';
import EventMarketingPage from '@/pages/events/manage/EventMarketingPage';
import EventMarketingMessagesPage from '@/pages/events/manage/EventMarketingMessagesPage';
import EventMarketingBatchPage from '@/pages/events/manage/EventMarketingBatchPage';
import EventAdminsPage from '@/pages/events/manage/EventAdminsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import ContactPage from '@/pages/ContactPage';
import CsaeStandardsPage from '@/pages/CsaeStandardsPage';

import AdminMembersPage, { AdminMembersAccess } from '@/pages/admin/AdminMembersPage';
import InformativePostsPage from '@/pages/admin/InformativePostsPage';
import BusinessReportPage from '@/pages/admin/BusinessReportPage';
import PointsDashboardPage from '@/pages/admin/PointsDashboardPage';
import ReportedPostsPage from '@/pages/admin/ReportedPostsPage';
import AdvertisementPage from '@/pages/admin/AdvertisementPage';
import CreateAdvertisementPage from '@/pages/admin/CreateAdvertisementPage';
import EditAdvertisementPage from '@/pages/admin/EditAdvertisementPage';
import CreateCampaignPage from '@/pages/admin/CreateCampaignPage';
import EditCampaignPage from '@/pages/admin/EditCampaignPage';
import AdStatsPage from '@/pages/admin/AdStatsPage';

import { ThemeProvider } from '@/components/theme-provider';
import { GuestGuard } from '@/components/auth/GuestGuard';
import { AuthGuard } from '@/components/auth/AuthGuard';

import { ErrorBoundary } from './components/ErrorBoundary';
import { Loader2 } from 'lucide-react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { NotificationPermissionProvider } from '@/providers/notification-permission-provider';
import { MobileAppUpdateProvider } from '@/providers/mobile-app-update-provider';
import { CallProvider } from '@/providers/call-provider';

function App() {
  useDeepLinkRedirect();
  return (
    <ThemeProvider defaultTheme="light" storageKey="milora-theme">
      <Router>
        <NotificationPermissionProvider>
          <MobileAppUpdateProvider>
            <CallProvider>
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <div className="min-h-screen flex items-center justify-center bg-background">
                      <div className="flex flex-col items-center gap-4 text-muted-foreground italic">
                        <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
                        <p className="text-sm font-medium animate-pulse tracking-wide">
                          Loading Milora...
                        </p>
                      </div>
                    </div>
                  }
                >
                  <NuqsAdapter>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route
                        path="/login"
                        element={
                          <GuestGuard>
                            <LoginPage />
                          </GuestGuard>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <GuestGuard>
                            <RegisterPage />
                          </GuestGuard>
                        }
                      />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route
                        path="/dashboard"
                        element={
                          <AuthGuard>
                            <DashboardPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/create-community"
                        element={
                          <AuthGuard>
                            <CreateCommunityPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/account-settings"
                        element={
                          <AuthGuard>
                            <UserSettingsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/notifications"
                        element={
                          <AuthGuard>
                            <NotificationsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <AuthGuard>
                            <ProfilePage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/profile/edit"
                        element={
                          <AuthGuard>
                            <EditProfilePage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/profile/:section"
                        element={
                          <AuthGuard>
                            <ProfilePage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/events"
                        element={
                          <AuthGuard>
                            <MyConclaveEventsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/events/create"
                        element={
                          <AuthGuard>
                            <CreateStandaloneConclavePage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/invite/:code" element={<InvitePage />} />
                      <Route path="/i/:code" element={<InvitePage />} />
                      <Route path="/u/:userId" element={<ProfilePage />} />
                      <Route
                        path="/calls/:callId"
                        element={
                          <AuthGuard>
                            <div className="min-h-screen bg-background" />
                          </AuthGuard>
                        }
                      />
                      <Route path="/b/:businessId" element={<ProfilePage />} />
                      <Route path="/ub/:userId/:businessId" element={<ProfilePage />} />
                      <Route
                        path="/posts/:postId"
                        element={
                          <AuthGuard>
                            <PostPage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/leads/:leadId" element={<LeadDetailPage />} />

                      <Route path="/:communityId" element={<CommunityLayout />}>
                        <Route path="invite" element={<InviteAcceptPage />} />
                        <Route path="u/:userId" element={<ProfilePage />} />
                        <Route path="b/:businessId" element={<ProfilePage />} />
                        <Route path="ub/:userId/:businessId" element={<ProfilePage />} />
                        <Route
                          path="sub-communities"
                          element={
                            <AuthGuard>
                              <PermissionGuard permission="create_subcommunity">
                                <SubCommunityPage />
                              </PermissionGuard>
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="create-sub-community"
                          element={
                            <AuthGuard>
                              <PermissionGuard permission="create_subcommunity">
                                <CreateSubCommunityPage />
                              </PermissionGuard>
                            </AuthGuard>
                          }
                        />

                        {sharedCommunityRoutes}

                        <Route
                          path="settings"
                          element={
                            <AuthGuard>
                              <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                <CommunitySettingsRouter />
                              </RoleGuard>
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="admin-members"
                          element={
                            <AuthGuard>
                              <AdminMembersAccess>
                                <AdminMembersPage />
                              </AdminMembersAccess>
                            </AuthGuard>
                          }
                        />

                        <Route
                          path="search"
                          element={
                            <AuthGuard>
                              <SocialSearchPage />
                            </AuthGuard>
                          }
                        />

                        <Route element={<CommunityKindRoute allowedKinds={['COMMUNITY']} />}>
                          <Route
                            path="posts/:postId"
                            element={
                              <AuthGuard>
                                <CommunityPostPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="posts/:postId/edit"
                            element={
                              <AuthGuard>
                                <EditPostPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="dashboard/projects/:projectId"
                            element={
                              <AuthGuard>
                                <VendorProjectDetailPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="members"
                            element={
                              <AuthGuard>
                                <MembersPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="gives-asks"
                            element={
                              <AuthGuard>
                                <GivesAsksPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events"
                            element={
                              <AuthGuard>
                                <EventsPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="projects"
                            element={
                              <AuthGuard>
                                <ProjectsPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="projects/new"
                            element={
                              <AuthGuard>
                                <CreateProjectPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="projects/:projectId"
                            element={
                              <AuthGuard>
                                <ProjectDetailsPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="projects/:projectId/edit"
                            element={
                              <AuthGuard>
                                <EditProjectPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create"
                            element={
                              <AuthGuard>
                                <CreatePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/lead"
                            element={
                              <AuthGuard>
                                <LeadPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/business"
                            element={
                              <AuthGuard>
                                <CreateBusinessPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/business/:businessId"
                            element={
                              <AuthGuard>
                                <CreateBusinessPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/testimonial"
                            element={
                              <AuthGuard>
                                <TestimonialPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/thank-you"
                            element={
                              <AuthGuard>
                                <ThankYouPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/meetup"
                            element={
                              <AuthGuard>
                                <MeetupPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/general"
                            element={
                              <AuthGuard>
                                <GeneralPostPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/poll"
                            element={
                              <AuthGuard>
                                <PollPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="create/event"
                            element={
                              <AuthGuard>
                                <CreateEventPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId"
                            element={
                              <AuthGuard>
                                <EventDetailsPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/conclave/live"
                            element={
                              <AuthGuard>
                                <ConclaveLivePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/conclave/profile"
                            element={
                              <AuthGuard>
                                <ConclaveNetworkingProfilePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/conclave/organise"
                            element={
                              <AuthGuard>
                                <ConclaveOrganisePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/edit"
                            element={
                              <AuthGuard>
                                <EditEventPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/manage"
                            element={
                              <AuthGuard>
                                <EventManagementPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/marketing"
                            element={
                              <AuthGuard>
                                <EventMarketingMessagesPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/marketing/new"
                            element={
                              <AuthGuard>
                                <EventMarketingPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/marketing/batch/:batchId"
                            element={
                              <AuthGuard>
                                <EventMarketingBatchPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/registrations"
                            element={
                              <AuthGuard>
                                <EventRegistrationsPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/admins"
                            element={
                              <AuthGuard>
                                <EventAdminsPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/activities"
                            element={
                              <AuthGuard>
                                <EventActivitiesPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/activities/:activityId/scanner"
                            element={
                              <AuthGuard>
                                <ActivityScannerPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="events/:eventId/activities/:activityId/self-mark"
                            element={
                              <AuthGuard>
                                <ActivitySelfMarkPage />
                              </AuthGuard>
                            }
                          />
                          <Route path="business" element={<BusinessPage />} />
                          <Route
                            path="profile"
                            element={
                              <AuthGuard>
                                <ProfilePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="profile/edit"
                            element={
                              <AuthGuard>
                                <EditProfilePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="profile/:section"
                            element={
                              <AuthGuard>
                                <ProfilePage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="membership"
                            element={
                              <AuthGuard>
                                <MembershipPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="membership-admin"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <MembershipAdminPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="requests"
                            element={
                              <AuthGuard>
                                <RoleGuard
                                  allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}
                                  allowedPermissions={['manage_new_members']}
                                >
                                  <MemberRequestsPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="informative-posts"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <InformativePostsPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="business-report"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <BusinessReportPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="points-dashboard"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <PointsDashboardPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="reported-posts"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <ReportedPostsPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="advertisement/campaign/:campaignId/stats"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <AdStatsPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="advertisement/campaign/:campaignId/edit"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <EditCampaignPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="advertisement/ad/:adId/campaign/new"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <CreateCampaignPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="advertisement/ad/:adId/edit"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <EditAdvertisementPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="advertisement/new"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <CreateAdvertisementPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="advertisement"
                            element={
                              <AuthGuard>
                                <RoleGuard allowedRoles={['ADMIN', 'CREATOR', 'OWNER']}>
                                  <AdvertisementPage />
                                </RoleGuard>
                              </AuthGuard>
                            }
                          />
                        </Route>
                        {communityPathFallbackRoute}
                      </Route>
                      <Route path="/admin/categories" element={<AdminCategoriesPage />} />
                      <Route
                        path="/create/business"
                        element={
                          <AuthGuard>
                            <CreateBusinessPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/create/business/:businessId"
                        element={
                          <AuthGuard>
                            <CreateBusinessPage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/debug-calendar" element={<DebugCalendarPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/csae" element={<CsaeStandardsPage />} />
                      <Route
                        path="/e/:eventId/conclave/live"
                        element={
                          <AuthGuard>
                            <ConclaveLivePage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/e/:eventId/conclave/profile"
                        element={
                          <AuthGuard>
                            <ConclaveNetworkingProfilePage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/e/:eventId/conclave/organise"
                        element={
                          <AuthGuard>
                            <ConclaveOrganisePage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/e/:eventId" element={<EventDetailsPage />} />
                    </Routes>
                  </NuqsAdapter>
                </Suspense>
              </ErrorBoundary>
              <OnboardingPrompt />
              <BusinessLinkCompliancePrompt />
              <PostRegisterBusinessPrompt />
              <Toaster />
            </CallProvider>
          </MobileAppUpdateProvider>
        </NotificationPermissionProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
